"use client";

import type { FC } from "react";
import type { ConsoleCloseReason } from "@akashnetwork/http-sdk";
import { CustomTooltip } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { systemCloseReasonOf } from "@src/components/activity/activityLabels/activityLabels";
import type { LeaseDto } from "@src/types/deployment";
import { isLeaseLive } from "@src/utils/leaseUtils";
import { classifyLeaseCloseReason, getClosedLeaseLabel, getClosedLeaseSummaryLabel, isClosedByOwner, isProviderReclaimed, isReclaiming } from "@src/utils/reclamationUtils";
import type { WorkloadStatus } from "./DeploymentPlacements/placementModel";
import { ReclamationCountdown } from "./ReclamationCountdown";
export type StatusTone = "running" | "pending" | "loading" | "warning" | "closed";
export const DEPENDENCIES = {
  CustomTooltip,
  ReclamationCountdown
};
const STATUS_LABELS: Record<string, string> = {
  active: "Running",
  closed: "Closed"
};
const STATUS_TONES: Record<string, StatusTone> = {
  active: "running",
  closed: "closed"
};
const BADGE_TONE_CLASS: Record<StatusTone, string> = {
  running: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  pending: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  loading: "bg-muted text-muted-foreground",
  warning: "bg-warning/10 text-warning",
  closed: "bg-muted text-muted-foreground"
};
export const DEPLOYMENT_STATUS_DOT_CLASSES: Record<StatusTone, string> = {
  running: "bg-emerald-500",
  pending: "bg-amber-500",
  loading: "bg-muted-foreground",
  warning: "bg-warning",
  closed: "bg-muted-foreground"
};
export interface DeploymentStatusView {
  label: string;
  summaryLabel: string;
  tone: StatusTone;
  isSpinningUp?: boolean;
  /** Why Console closed the deployment, which its label alone cannot say. */
  reason?: string;
}
export interface DeploymentStatusBadgeProps {
  status: DeploymentStatusView;
  leases?: LeaseDto[] | null;
  isSummarized?: boolean;
  className?: string;
  dependencies?: typeof DEPENDENCIES;
}
const CLOSING_STATUS: DeploymentStatusView = {
  label: "Closing",
  summaryLabel: "Closing",
  tone: "loading"
};
const UPDATING_STATUS: DeploymentStatusView = {
  label: "Updating",
  summaryLabel: "Updating",
  tone: "loading"
};
const RECLAIMING_STATUS: DeploymentStatusView = {
  label: "Reclaiming",
  summaryLabel: "Reclaiming",
  tone: "warning"
};
const CLOSED_BY_CONSOLE_LABEL = "Closed by Console";

/** From the lease landing until every service has a ready replica, whichever part of that the deployment is in. */
export const SPINNING_UP_STATUS: DeploymentStatusView = {
  label: "Spinning up",
  summaryLabel: "Spinning up",
  tone: "loading",
  isSpinningUp: true
};
export const FINDING_PROVIDER_STATUS: DeploymentStatusView = {
  label: "Finding a provider",
  summaryLabel: "Finding a provider",
  tone: "loading",
  isSpinningUp: true
};
export const STARTING_STATUS: DeploymentStatusView = {
  label: "Starting",
  summaryLabel: "Starting",
  tone: "loading",
  isSpinningUp: true
};
export const CREATING_STATUS: DeploymentStatusView = {
  label: "Creating",
  summaryLabel: "Creating",
  tone: "loading"
};
const WORKLOAD_STATUSES: Partial<Record<WorkloadStatus, DeploymentStatusView>> = {
  starting: SPINNING_UP_STATUS,
  loading: {
    label: "Loading",
    summaryLabel: "Loading",
    tone: "loading"
  },
  unreachable: {
    label: "Provider not responding",
    summaryLabel: "Provider not responding",
    tone: "warning"
  }
};

/**
 * A deployment stays `active` on chain after its last lease dies — closed by the provider, reclaimed, or out
 * of funds — so the chain state alone would keep claiming "Running" over a workload that is long gone. When
 * no lease is live, the badge speaks for the lease instead, reusing the same close-reason copy the
 * reclamation banner and deployment list show. A dead lease under a deployment that is still open reads as a
 * warning: the escrow is live and a redeploy brings the workload back, while a deployment that is itself
 * closed has nothing left to act on and reads as muted. A lease inside its reclamation grace period is live
 * but doomed, so it reads as "Reclaiming" rather than "Running". A live lease is only "Running" once its
 * provider reports a ready replica for every service, when the caller passes what the providers report.
 */
export function getDeploymentStatus(state: string, leases?: LeaseDto[] | null, isClosing = false, workload?: WorkloadStatus, isUpdating = false, consoleCloseReason?: ConsoleCloseReason | null): DeploymentStatusView {
  if (isClosing && state === "active") return CLOSING_STATUS;
  if (isUpdating && state === "active") return UPDATING_STATUS;
  const deploymentTone = STATUS_TONES[state] ?? "pending";
  const deadLease = leases?.length && !leases.some(isLeaseLive) ? selectLeaseToReportOn(leases) : undefined;
  if (!deadLease) {
    if (leases?.some(isReclaiming)) return RECLAIMING_STATUS;
    const workloadStatus = state === "active" && workload ? WORKLOAD_STATUSES[workload] : undefined;
    if (workloadStatus) return workloadStatus;
    if (consoleCloseReason && state === "closed" && !leases?.length) return closedByConsoleStatusOf(consoleCloseReason, deploymentTone);
    const label = STATUS_LABELS[state] ?? state;
    return {
      label,
      summaryLabel: label,
      tone: deploymentTone
    };
  }
  const tone = deploymentTone === "closed" ? "closed" : "warning";
  if (consoleCloseReason && isClosedByOwner(deadLease)) return closedByConsoleStatusOf(consoleCloseReason, tone);
  return {
    label: getClosedLeaseLabel(deadLease),
    summaryLabel: getClosedLeaseSummaryLabel(deadLease),
    tone
  };
}

/** Console closes with the owner's wallet, so a close that reads as the owner's, or a closed deployment with no lease to read, is Console's once the api says why. */
function closedByConsoleStatusOf(consoleCloseReason: ConsoleCloseReason, tone: StatusTone): DeploymentStatusView {
  return {
    label: CLOSED_BY_CONSOLE_LABEL,
    summaryLabel: CLOSED_BY_CONSOLE_LABEL,
    tone,
    reason: systemCloseReasonOf(consoleCloseReason)
  };
}

/**
 * Which of several closed leases the badge speaks for. A provider close wins over a tenant close: it is the
 * one the owner did not ask for and can act on by redeploying, and picking it keeps the label stable instead
 * of following whatever order the lease list arrived in.
 */
function selectLeaseToReportOn(leases: LeaseDto[]): LeaseDto {
  const closedByProvider = leases.find(lease => classifyLeaseCloseReason(lease.reason ?? lease.reclamation?.reason) === "provider" || isProviderReclaimed(lease));
  return closedByProvider ?? leases[0];
}
export interface StatusBadgeProps {
  label: string;
  tone: StatusTone;
  className?: string;
}
export const StatusBadge: FC<StatusBadgeProps> = ({
  label,
  tone,
  className
}) => <span className={cn("inline-flex items-center gap-2 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium", BADGE_TONE_CLASS[tone], className)} data-sentry-component="StatusBadge" data-sentry-source-file="DeploymentStatusBadge.tsx">
    {tone === "loading" ? <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" /> : <span className="relative flex h-2 w-2">
        {tone === "running" && <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", DEPLOYMENT_STATUS_DOT_CLASSES[tone])} />}
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", DEPLOYMENT_STATUS_DOT_CLASSES[tone])} />
      </span>}
    {label}
  </span>;
export const DeploymentStatusBadge: FC<DeploymentStatusBadgeProps> = ({
  status,
  leases,
  isSummarized,
  className,
  dependencies: d = DEPENDENCIES
}) => {
  const {
    label,
    summaryLabel,
    tone
  } = status;
  const isShortened = !!isSummarized && summaryLabel !== label;
  const isBeingReclaimed = label === RECLAIMING_STATUS.label;
  if (!isShortened && !isBeingReclaimed) return <StatusBadge label={label} tone={tone} className={className} />;
  return <d.CustomTooltip title={<div className="space-y-1">
          {isShortened && <p>{label}</p>}
          <d.ReclamationCountdown leases={leases} />
        </div>} data-sentry-element="d.CustomTooltip" data-sentry-component="DeploymentStatusBadge" data-sentry-source-file="DeploymentStatusBadge.tsx">
      <div className="inline-flex">
        <StatusBadge label={isShortened ? summaryLabel : label} tone={tone} className={cn("cursor-help", className)} data-sentry-element="StatusBadge" data-sentry-source-file="DeploymentStatusBadge.tsx" />
      </div>
    </d.CustomTooltip>;
};